import { useEffect } from 'react';
import {
  Close as CloseIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { FileItem, FileUploader, downloadFileFromUrl } from '@/common';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';
import { downloadFileFromTeamMember } from '../api/downloadFileFromTeamMember';
import { downloadFileFromResponse, getMessageFromError } from '@/core';
import { addFileToTeamMember } from '../api/addFileToTeamMember';
import { downloadFileFromDws } from '../api/downloadFileFromDs';

type MyCostsFilesModalScreen = 'file-list' | 'add-files';

type MyCostsFilesModalProps = {
  show: boolean;
  setShow: (show: boolean) => void;
  screen: MyCostsFilesModalScreen;
  setScreen: (value: MyCostsFilesModalScreen) => void;
};

const MyCostsFilesModal = ({ show, setShow, screen, setScreen }: MyCostsFilesModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { auditorVisit, invalidateAuditorVisit } = useAuditorVisitDetailContext();

  const downloadFileMutation = useMutation({
    mutationFn: downloadFileFromTeamMember,
    onSuccess: async (response) => {
      await downloadFileFromResponse(response);
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const downloadFileFromDsMutation = useMutation({
    mutationFn: downloadFileFromDws,
    onSuccess: async (result) => {
      const res = result.res;
      const blob = await res.blob();
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.filename);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  useEffect(() => {
    // Hacky way to solve flickering - file-list is shown before modal closing animation is fully finished
    const timeout = setTimeout(() => {
      if (!show) {
        setScreen('file-list');
      }
    }, 100);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={show}
      onClose={() => {
        setShow(false);
        setScreen('file-list');
      }}
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5">
          {screen === 'file-list' ? (
            t('My costs - Files', { defaultValue: 'My costs - Files' })
          ) : (
            <Box>
              <IconButton onClick={() => setScreen('file-list')} sx={{ p: 0, mr: '0.5rem' }}>
                <ArrowBackIcon />
              </IconButton>
              {t('My costs - Upload files', { defaultValue: 'My costs - Upload files' })}
            </Box>
          )}
        </Typography>
        <CloseIcon
          sx={{ fontSize: '2rem', cursor: 'pointer' }}
          onClick={() => {
            setShow(false);
            setScreen('file-list');
          }}
        />
      </DialogTitle>
      <DialogContent>
        {screen === 'file-list' ? (
          <>
            <Button
              onClick={() => setScreen('add-files')}
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth
            >
              {t('Add files', { defaultValue: 'Add files' })}
            </Button>
            <Box>
              {auditorVisit.myCosts.files.length > 0 ? (
                <List>
                  {auditorVisit.myCosts.files.map((item) => (
                    <FileItem
                      key={item.id}
                      name={item.title || ''}
                      uploadedBy={item.uploadedBy}
                      uploadedAt={item.uploadedAt}
                      storeType={item.storeType}
                      fileName={item.fileName}
                      onDownload={() => {
                        if (item.storeType === 'DocumentSystem' && item.dwsDok) {
                          downloadFileFromDsMutation.mutate({
                            dok: item.dwsDok,
                            filename: item.title || item.dwsDok.originalfilename,
                          });
                        } else {
                          downloadFileMutation.mutate({
                            id: auditorVisit.teamMemberId,
                            fileId: item.id,
                          });
                        }
                      }}
                    />
                  ))}
                </List>
              ) : (
                <Typography variant="subtitle1">
                  {t('No files', { defaultValue: 'No files' })}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <FileUploader
            onFileUpload={async (file) => {
              const formData = new FormData();

              formData.append('title', file.file.name);
              formData.append('attachments', file.file);

              // eslint-disable-next-line no-promise-executor-return
              await new Promise((r) => setTimeout(r, 800));

              return addFileToTeamMember({ id: auditorVisit.teamMemberId, formData });
            }}
            onUploadFinished={(allFilesSuccessfullyUploaded) => {
              invalidateAuditorVisit();

              if (allFilesSuccessfullyUploaded) {
                setScreen('file-list');
              }
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MyCostsFilesModal;
